import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import JsonViewer, { type JsonValue } from "@galaxy-io/dls/json/JsonViewer";
import Box from "@galaxy-io/dls/layout/Box";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Tabs from "@galaxy-io/dls/navigation/Tabs";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { pageNumber } from "@gtm/contracts";
import { deliverySortKeys } from "@gtm/contracts/core/sorting";
import {
  ArrowClockwiseIcon,
  ClockCounterClockwiseIcon,
  CodeIcon,
  WebhooksLogoIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DrawerList, DrawerSection, DrawerStack } from "@/components/workspace/drawer-content";
import { CompactSelect, Inline, SearchForm } from "@/components/workspace/people-styles";
import { RecordAudit } from "@/components/workspace/record-audit";
import { RecordQueries } from "@/components/workspace/record-queries";
import { RecordTable } from "@/components/workspace/record-table";
import { SelectField } from "@/components/workspace/select-field";
import { SourceLabel } from "@/components/workspace/source-icon";
import { Status } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { useTableSort } from "@/components/workspace/use-table-sort";
import { apiClient, unwrap } from "@/lib/api-client";
import { TIME_RANGE_OPTIONS } from "@/lib/filters";
import { deliveryQueries } from "@/lib/record-queries";
import { toastDefaults } from "@/lib/toast";
import { date } from "@/lib/utils";

const RETRYABLE = ["failed", "unknown", "cancelled"];
const CANCELLABLE = ["pending"];
/** `sent` has nothing left to act on, so it stays unselectable. */
const ACTIONABLE = [...RETRYABLE, ...CANCELLABLE];
export function DeliveryHistory() {
  const sort = useTableSort(deliverySortKeys);
  const { params, update } = useUrlState();
  const client = useQueryClient(),
    { toast } = useToast();
  const page = pageNumber(params.get("page") || undefined),
    status = params.get("status") || "",
    q = params.get("q") || "",
    destination = params.get("destination") || "",
    days = params.get("days") || "",
    selected = params.get("deliveryId") || "";
  const selectedTab = params.get("deliveryTab");
  const tab = selectedTab === "queries" || selectedTab === "audit" ? selectedTab : "overview";
  const filtering = !!(status || q || destination || days);
  const [busy, setBusy] = useState(false);
  function navigate(values: Record<string, string>) {
    update(Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value || null])));
  }
  const list = useQuery({
    queryKey: ["webhook-deliveries", page, status, q, destination, days, sort.query],
    queryFn: () =>
      unwrap(
        apiClient().GET("/deliveries", {
          params: {
            query: {
              page: String(page),
              status,
              q,
              destination,
              days,
              ...sort.query,
            },
          },
        }),
      ),
    placeholderData: (previous) => previous,
    refetchInterval: 5000,
  });
  const destinations = Array.from(
    new Set([...(list.data?.destinations || []), destination].filter(Boolean)),
  );
  const detail = useQuery({
    queryKey: ["webhook-delivery", selected],
    queryFn: () =>
      unwrap(
        apiClient().GET("/deliveries/{id}", {
          params: { path: { id: selected } },
        }),
      ),
    enabled: !!selected,
    refetchInterval: 5000,
  });
  async function action(action: "retry" | "retry_current" | "cancel") {
    setBusy(true);
    try {
      await unwrap(
        apiClient().POST("/deliveries/{id}/actions", {
          params: { path: { id: selected } },
          body: { action },
        }),
      );
      await Promise.all([
        client.invalidateQueries({ queryKey: ["webhook-deliveries"] }),
        client.invalidateQueries({ queryKey: ["webhook-delivery", selected] }),
      ]);
      toast({
        variant: ToastVariant.SUCCESS,
        header: action !== "cancel" ? "Delivery retried" : "Delivery cancelled",
        description:
          action !== "cancel"
            ? "Temporal runs the delivery again shortly."
            : "No further attempts are made.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } catch (e) {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not update delivery",
        description: (e as Error).message,
        ...toastDefaults(ToastVariant.ERROR),
      });
    } finally {
      setBusy(false);
    }
  }
  const statuses = new Map((list.data?.rows || []).map((d) => [d.id, d.status] as const));
  async function bulkAction(ids: string[], action: "retry" | "cancel", clear: () => void) {
    setBusy(true);
    try {
      const result = await unwrap(
        apiClient().POST("/deliveries/actions", { body: { ids, action } }),
      );
      await client.invalidateQueries({ queryKey: ["webhook-deliveries"] });
      if (result.succeeded.length) clear();
      toast({
        variant: result.failed.length ? ToastVariant.WARNING : ToastVariant.SUCCESS,
        header: `${result.succeeded.length} of ${ids.length} ${
          action === "cancel" ? "cancelled" : "retried"
        }`,
        description: result.failed.length
          ? result.failed[0].error
          : action === "cancel"
            ? "No further attempts are made."
            : "Temporal runs the deliveries again shortly.",
        ...toastDefaults(result.failed.length ? ToastVariant.WARNING : ToastVariant.SUCCESS),
      });
    } catch (e) {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not update deliveries",
        description: (e as Error).message,
        ...toastDefaults(ToastVariant.ERROR),
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <BaseToolbar
        leadingActions={
          <>
            <SearchForm
              onSubmit={(event) => {
                event.preventDefault();
                navigate({
                  q: String(new FormData(event.currentTarget).get("q") || ""),
                  page: "",
                });
              }}
            >
              <Input
                key={q}
                name="q"
                aria-label="Search deliveries"
                placeholder="Search records, destinations, errors…"
                defaultValue={q}
                type="search"
                size={InputSize.SMALL}
                label="Search deliveries"
                hideLabel
              />
            </SearchForm>
            <CompactSelect $width={150}>
              <SelectField
                label="Delivery status"
                compact
                value={status}
                onChange={(s) => navigate({ status: s, page: "" })}
                options={[
                  { value: "", label: "All statuses" },
                  ...["pending", "sent", "failed", "unknown", "cancelled"].map((value) => ({
                    value,
                    label: value[0].toUpperCase() + value.slice(1),
                  })),
                ]}
                hideLabel
              />
            </CompactSelect>
            <CompactSelect $width={200}>
              <SelectField
                label="Destination"
                compact
                search
                value={destination}
                onChange={(d) => navigate({ destination: d, page: "" })}
                options={[
                  { value: "", label: "All destinations" },
                  ...destinations.map((name) => ({ value: name, label: name })),
                ]}
                hideLabel
              />
            </CompactSelect>
            <CompactSelect $width={130}>
              <SelectField
                label="Time range"
                compact
                value={days}
                onChange={(d) => navigate({ days: d, page: "" })}
                options={TIME_RANGE_OPTIONS}
                hideLabel
              />
            </CompactSelect>
            {filtering && (
              <Button
                label="Clear"
                ariaLabel="Clear delivery filters"
                icon={XIcon}
                variant={ButtonVariant.TERTIARY}
                onClick={() =>
                  navigate({
                    q: "",
                    status: "",
                    destination: "",
                    days: "",
                    page: "",
                  })
                }
                size={ButtonSize.SMALL}
                tooltip="Clear delivery filters"
              />
            )}
          </>
        }
        trailingActions={
          <Button
            label="Refresh"
            ariaLabel="Refresh deliveries"
            icon={ArrowClockwiseIcon}
            variant={ButtonVariant.SECONDARY}
            isLoading={list.isFetching}
            isDisabled={list.isFetching}
            onClick={() => {
              void list.refetch();
            }}
            size={ButtonSize.SMALL}
            tooltip="Refresh deliveries"
          />
        }
      />
      <RecordTable
        sort={sort}
        label="Deliveries"
        auditKind="delivery"
        rowKeys={(list.data?.rows || []).map((d) => d.id)}
        isLoading={list.isPending || list.isPlaceholderData}
        error={list.error || undefined}
        onRetry={() => list.refetch()}
        columns={[
          { key: "record", label: "Record", width: 260, pin: "left" },
          { key: "destination", label: "Destination", width: 200 },
          { key: "state", label: "State", width: 150 },
          { key: "status", label: "Delivery", width: 140 },
          { key: "attempts", label: "Attempts", width: 100 },
          { key: "created", label: "Queued", width: "auto", align: "right" as const },
        ]}
        bulk={{
          isSelectable: (id) => ACTIONABLE.includes(statuses.get(id) || ""),
          actions: (selected, clear) => {
            const retryable = selected.filter((id) => RETRYABLE.includes(statuses.get(id) || ""));
            const cancellable = selected.filter((id) =>
              CANCELLABLE.includes(statuses.get(id) || ""),
            );
            return (
              <>
                <Button
                  label={`Retry selected${retryable.length ? ` (${retryable.length})` : ""}`}
                  isDisabled={busy || !retryable.length}
                  onClick={() => bulkAction(retryable, "retry", clear)}
                />
                <Button
                  label={`Cancel selected${cancellable.length ? ` (${cancellable.length})` : ""}`}
                  variant={ButtonVariant.SECONDARY}
                  isDisabled={busy || !cancellable.length}
                  onClick={() => bulkAction(cancellable, "cancel", clear)}
                />
                <Button
                  label="Clear selection"
                  variant={ButtonVariant.TERTIARY}
                  isDisabled={!selected.length}
                  onClick={clear}
                />
              </>
            );
          },
        }}
        rows={(list.data?.rows || []).map((d) => ({
          record: (
            <Button
              label={d.title || d.id}
              variant={ButtonVariant.TERTIARY}
              onClick={() => navigate({ deliveryId: d.id })}
            />
          ),
          destination: <SourceLabel source={d.provider} label={d.destination_name} />,
          state: <Status value={d.state} />,
          status: <Status value={d.status} />,
          attempts: d.attempts,
          created: date(d.created_at, true),
        }))}
        paging={{
          page,
          hasNext: !!list.data?.hasNext,
          total: list.data?.total,
          hasPrevious: page > 1,
          busy: list.isFetching,
          onNext: () => navigate({ page: String(page + 1) }),
          onPrevious: () => navigate({ page: String(page - 1) }),
        }}
      />
      <SurfaceDialog
        open={!!selected}
        drawer
        flush
        title="Delivery"
        headerIcon={WebhooksLogoIcon}
        onClose={() => navigate({ deliveryId: "", deliveryTab: "" })}
        busy={busy}
        headerActions={
          detail.data && (
            <Inline>
              {detail.data.payload.record.type === "person" &&
                ["failed", "unknown", "cancelled"].includes(detail.data.status) && (
                  <>
                    <Button
                      size={ButtonSize.SMALL}
                      label="Retry delivery"
                      isDisabled={busy}
                      onClick={() => action("retry")}
                    />
                    <Button
                      size={ButtonSize.SMALL}
                      label="Retry with current connection"
                      variant={ButtonVariant.SECONDARY}
                      isDisabled={busy}
                      onClick={() => action("retry_current")}
                    />
                  </>
                )}
              {detail.data.status === "queued" && (
                <Button
                  size={ButtonSize.SMALL}
                  label="Cancel delivery"
                  variant={ButtonVariant.SECONDARY}
                  isDisabled={busy}
                  onClick={() => action("cancel")}
                />
              )}
            </Inline>
          )
        }
      >
        {detail.isPending ? (
          <QueryState loadingMessage="Loading delivery…" />
        ) : detail.error ? (
          <QueryState error={detail.error} onRetry={() => void detail.refetch()} />
        ) : (
          detail.data && (
            <>
              <Tabs
                ariaLabel="Delivery sections"
                value={tab}
                onChange={(value) => update({ deliveryTab: value === "overview" ? null : value })}
                items={[
                  { id: "overview", label: "Overview" },
                  { id: "queries", label: "Queries" },
                  { id: "audit", label: "Audit" },
                ]}
              />
              <DrawerStack>
                {tab === "queries" ? (
                  <RecordQueries
                    queries={deliveryQueries({
                      id: detail.data.id,
                      recordKey: (list.data?.rows || []).find((row) => row.id === selected)
                        ?.record_key,
                    })}
                  />
                ) : tab === "audit" ? (
                  <>
                    <RecordAudit kind="delivery" id={detail.data.id} />
                    <DrawerSection header="Attempts" icon={ClockCounterClockwiseIcon} isFlush>
                      <RecordTable
                        sort={sort}
                        label="Delivery attempts"
                        auditKind="attempt"
                        rowKeys={detail.data.attempts.map((a) => a.id)}
                        fillHeight={false}
                        columns={[
                          { key: "attempt", label: "Attempt", width: 80 },
                          { key: "status", label: "Result", width: 140 },
                          { key: "http", label: "HTTP", width: 80 },
                          { key: "started", label: "Started", width: "auto" },
                        ]}
                        rows={detail.data.attempts.map((a) => ({
                          attempt: a.attempt,
                          status: <Status value={a.status} />,
                          http: a.http_status || "—",
                          started: date(a.started_at, true),
                        }))}
                      />
                    </DrawerSection>
                  </>
                ) : (
                  <>
                    <DrawerList>
                      <DescriptionList
                        items={[
                          {
                            label: "Status",
                            value: (
                              <>
                                <Status value={detail.data.status} />
                              </>
                            ),
                          },
                          { label: "Destination", value: detail.data.destination },
                          { label: "Event", value: detail.data.payload.event },
                          { label: "Delivery ID", value: detail.data.id },
                        ]}
                      />
                    </DrawerList>
                    {detail.data.error && (
                      <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                        {detail.data.error}
                      </Text>
                    )}
                    {detail.data.status === "unknown" && (
                      <Text size={TextSize.BODY_SM}>
                        The receiver may have accepted this delivery. Retrying uses the same
                        idempotency key.
                      </Text>
                    )}

                    <DrawerSection header="Payload" icon={CodeIcon} defaultIsOpen>
                      <Box maxHeight={440} overflow="auto">
                        <JsonViewer
                          ariaLabel="Delivery payload"
                          data={
                            (detail.data.payload.outboundBody ?? detail.data.payload) as JsonValue
                          }
                          canSearch
                          canCopy
                        />
                      </Box>
                    </DrawerSection>
                  </>
                )}
              </DrawerStack>
            </>
          )
        )}
      </SurfaceDialog>
    </>
  );
}
